import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, Stat, Empty } from "@/components/admin/Ui";
import { UsersThree, FileText, UsersFour, EnvelopeSimple, ChatCircleDots, Megaphone, ArrowUpRight } from "@phosphor-icons/react";

export default function AdminOverview() {
  const [stats, setStats] = useState(null);
  const [recentLeads, setRecentLeads] = useState([]);
  const [recentApps, setRecentApps] = useState([]);

  useEffect(() => {
    (async () => {
      const [s, l, a] = await Promise.all([
        api.get("/stats"),
        api.get("/leads"),
        api.get("/applications"),
      ]);
      setStats(s.data);
      setRecentLeads((l.data.leads || []).slice(0, 5));
      setRecentApps((a.data.applications || []).slice(0, 5));
    })();
  }, []);

  return (
    <div data-testid="admin-overview-page">
      <PageHeader title="Network operations" subtitle="Overview" />
      <div className="p-8 space-y-8">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Stat label="Leads" value={stats?.leads_total ?? "—"} hint={`${stats?.leads_qualified ?? 0} qualified`} />
          <Stat label="Applications" value={stats?.applications_total ?? "—"} hint={`${stats?.applications_pending ?? 0} pending`} />
          <Stat label="Members" value={stats?.members_total ?? "—"} hint="active" />
          <Stat label="Outreach" value={stats?.outreach_total ?? "—"} hint={`${stats?.outreach_sent ?? 0} sent`} />
          <Stat label="Replies" value={stats?.replies_total ?? "—"} hint="classified" />
          <Stat label="Content" value={stats?.content_total ?? "—"} hint="generated" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Panel title="Recent leads" link="/admin/leads" icon={UsersThree}>
            {recentLeads.length === 0 ? <Empty label="No leads yet. Run the Research Agent." /> : (
              <ul className="divide-y divide-black/10">
                {recentLeads.map((l) => (
                  <li key={l.id} className="py-3 flex items-center justify-between text-sm" data-testid={`overview-lead-${l.id}`}>
                    <div>
                      <div className="font-medium text-navy">{l.company_name}</div>
                      <div className="text-navy/60 text-xs">{l.country} · {l.status}</div>
                    </div>
                    <div className="ship-number text-lg font-bold text-ember">{l.score}</div>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
          <Panel title="Recent applications" link="/admin/applications" icon={FileText}>
            {recentApps.length === 0 ? <Empty label="No applications yet." /> : (
              <ul className="divide-y divide-black/10">
                {recentApps.map((a) => (
                  <li key={a.id} className="py-3 flex items-center justify-between text-sm" data-testid={`overview-application-${a.id}`}>
                    <div>
                      <div className="font-medium text-navy">{a.company_name}</div>
                      <div className="text-navy/60 text-xs">{a.country} · {a.status}</div>
                    </div>
                    <div className="text-xs text-navy/60">{a.recommendation}</div>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <QuickLink to="/admin/research" label="Run Research Agent" icon={UsersThree} />
          <QuickLink to="/admin/outreach" label="Draft Outreach" icon={EnvelopeSimple} />
          <QuickLink to="/admin/replies" label="Classify Reply" icon={ChatCircleDots} />
          <QuickLink to="/admin/content" label="Generate Content" icon={Megaphone} />
        </div>
      </div>
    </div>
  );
}

const Panel = ({ title, link, icon: Icon, children }) => (
  <div className="bg-white border border-black/10">
    <div className="flex items-center justify-between px-5 py-3 border-b border-black/10">
      <div className="flex items-center gap-2 text-navy">
        <Icon size={16} weight="duotone" className="text-ember" />
        <span className="font-semibold text-sm">{title}</span>
      </div>
      <Link to={link} className="text-xs uppercase tracking-widest text-navy/60 hover:text-ember flex items-center gap-1">
        Open <ArrowUpRight size={12} />
      </Link>
    </div>
    <div className="px-5 py-3">{children}</div>
  </div>
);

const QuickLink = ({ to, label, icon: Icon }) => (
  <Link to={to} data-testid={`quicklink-${label.toLowerCase().replace(/\s+/g, "-")}`} className="bg-white border border-black/10 p-5 hover:border-navy transition-colors flex flex-col gap-3 group">
    <Icon size={20} weight="duotone" className="text-ember" />
    <div className="font-semibold text-navy text-sm">{label}</div>
    <ArrowUpRight size={14} className="text-navy/40 group-hover:text-ember transition-colors self-end" />
  </Link>
);
